import { Input } from '@xefi/x-react/form';
import { mergeTailwindClasses } from '@xefi/x-react/utils';
import type { ChangeEventHandler, ComponentProps } from 'react';
import { forwardRef, useEffect, useState } from 'react';

type InputWithMaxLengthProps = ComponentProps<typeof Input> & {
   maxLength: number;
   containerClassName?: string;
};

export const InputWithMaxLength = forwardRef<
   HTMLInputElement,
   InputWithMaxLengthProps
>(
   (
      {
         containerClassName,
         maxLength,
         value,
         defaultValue,
         classNames,
         onChange,
         ...rest
      },
      ref,
   ) => {
      const isControlled = typeof value !== 'undefined';

      const [internalValue, setInternalValue] = useState<string>(() =>
         isControlled ? String(value ?? '') : String(defaultValue ?? ''),
      );

      const currentString = isControlled ? String(value ?? '') : internalValue;
      const currentLength = currentString.length;
      const isLimitReached = currentLength >= maxLength;

      const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
         if (!isControlled) setInternalValue(event.target.value);
         onChange?.(event);
      };

      useEffect(() => {
         if (!isControlled) {
            setInternalValue(String(defaultValue ?? ''));
         }
      }, [defaultValue, isControlled]);

      return (
         <div className={mergeTailwindClasses('relative', containerClassName)}>
            <Input
               ref={ref}
               value={value}
               defaultValue={defaultValue}
               onChange={handleChange}
               classNames={{
                  ...classNames,
                  inputWrapper: mergeTailwindClasses(
                     classNames?.inputWrapper,
                     'pr-[52px]',
                     maxLength > 99 && 'pr-[62px]',
                     maxLength > 999 && 'pr-[72px]',
                  ),
               }}
               maxLength={maxLength}
               {...rest}
            />
            <div
               className={mergeTailwindClasses(
                  'absolute right-1.5 bottom-1 text-right text-xs',
                  isLimitReached ? 'text-primary' : 'text-content1-400',
               )}
               role='status'
               aria-live='polite'
            >
               <span>{currentLength}</span>
               {'/'}
               <span>{maxLength}</span>
            </div>
         </div>
      );
   },
);

InputWithMaxLength.displayName = 'InputWithMaxLength';
